import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import VenueCard from "../components/VenueCard";
import TourCard from "../components/TourCard";
import SplitDirectoryView from "../components/SplitDirectoryView";
import { api } from "../lib/api";
import { List, Map as MapIcon } from "lucide-react";
import { SkeletonGrid } from "../components/Skeleton";

const TITLES = {
  restaurant: "Restaurants in Airlie Beach",
  bar: "Bars",
  cafe: "Cafés & Brunch",
  activity: "Things to do in Airlie Beach",
  nightlife: "Clubs & Party Places",
  "eat-drink": "Eat & Drink in Airlie Beach",
  accommodation: "Accommodation",
  all: "All Venues",
};

const EAT_DRINK_PILLS = [
  { slug: "", label: "All" },
  { slug: "restaurant", label: "Restaurant" },
  { slug: "bar", label: "Bar" },
  { slug: "cafe", label: "Café" },
];

const ACTIVITY_PILLS = [
  { slug: "", label: "All" },
  { slug: "sail", label: "Sailing" },
  { slug: "reef", label: "Reef" },
  { slug: "whitehaven", label: "Whitehaven" },
  { slug: "skydive", label: "Skydive" },
  { slug: "scenic", label: "Scenic flight" },
  { slug: "sunset_sail", label: "Sunset" },
  { slug: "eco", label: "Eco" },
];

export default function Directory() {
  const { category } = useParams();
  const [params, setParams] = useSearchParams();
  const [venues, setVenues] = useState([]);
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list"); // list | map
  const q = params.get("q") || "";
  const location = params.get("location") || "";
  const openNow = params.get("open_now") === "1";
  const activityType = params.get("type") || "";
  const subCategory = params.get("sub") || "";
  const isToursMode = category === "activity";
  const isEatDrinkMode = category === "eat-drink";

  useEffect(() => {
    setLoading(true);
    if (isToursMode) {
      // PLAY page = tours/experiences first, not operators.
      const p = new URLSearchParams();
      if (activityType) p.set("activity_type", activityType);
      if (q) p.set("q", q);
      api.get(`/tours?${p.toString()}`)
        .then((r) => setTours(r.data))
        .finally(() => setLoading(false));
      return;
    }
    const p = new URLSearchParams();
    if (isEatDrinkMode) {
      // Aggregate restaurant + bar + cafe. Optional sub-filter narrows to one.
      const cats = subCategory ? [subCategory] : ["restaurant", "bar", "cafe"];
      Promise.all(cats.map((c) => {
        const pp = new URLSearchParams();
        pp.set("category", c);
        if (q) pp.set("q", q);
        if (location) pp.set("location", location);
        if (openNow) pp.set("open_now", "true");
        return api.get(`/venues?${pp.toString()}`);
      }))
        .then((rs) => {
          const merged = rs.flatMap((r) => r.data);
          // Dedupe by id in case a venue has extra_categories overlap.
          const seen = new Set();
          const unique = merged.filter((v) => (seen.has(v.id) ? false : (seen.add(v.id), true)));
          unique.sort((a, b) => (b.is_sponsored - a.is_sponsored) || ((b.rating || 0) - (a.rating || 0)));
          setVenues(unique);
        })
        .finally(() => setLoading(false));
      return;
    }
    if (category && category !== "all") p.set("category", category);
    if (q) p.set("q", q);
    if (location) p.set("location", location);
    if (openNow) p.set("open_now", "true");
    api.get(`/venues?${p.toString()}`)
      .then((r) => setVenues(r.data))
      .finally(() => setLoading(false));
  }, [category, q, location, openNow, activityType, subCategory, isToursMode, isEatDrinkMode]);

  const setFilter = (k, v) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    setParams(p);
  };

  const total = isToursMode ? tours.length : venues.length;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-10 flex items-end justify-between flex-wrap gap-4">
          <div>
            <span className="text-sm tracking-widest text-coastal uppercase">{isToursMode ? "Play" : "Directory"}</span>
            <h1 className="font-heading text-4xl lg:text-5xl text-navy tracking-tight mt-2" data-testid="directory-title">
              {TITLES[category] || "Venues"}
            </h1>
            <p className="text-neutral-500 mt-2">
              {isToursMode ? `${total} experiences from local operators` : `${total} places to explore`}
            </p>
          </div>
          {/* View toggle — venues only; tours always render as a grid */}
          {!isToursMode && (
            <div className="inline-flex items-center bg-white border border-black/10 rounded-full p-1" role="tablist" data-testid="view-toggle">              <button
                onClick={() => setView("list")}
                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm transition-colors ${view === "list" ? "bg-navy text-white" : "text-neutral-600 hover:text-navy"}`}
                data-testid="view-list"
              >
                <List className="w-4 h-4" /> List
              </button>
              <button
                onClick={() => setView("map")}
                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm transition-colors ${view === "map" ? "bg-navy text-white" : "text-neutral-600 hover:text-navy"}`}
                data-testid="view-map"
              >
                <MapIcon className="w-4 h-4" /> Map
              </button>
            </div>
          )}
        </div>

        {/* Filters */}
        {isToursMode ? (
          <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="activity-type-filter">
            {ACTIVITY_PILLS.map((p) => {
              const active = (p.slug || "") === activityType;
              return (
                <button
                  key={p.slug || "all"}
                  onClick={() => setFilter("type", p.slug)}
                  className={`px-4 py-1.5 rounded-full text-sm transition-colors ${active ? "bg-navy text-white" : "bg-white border border-black/10 text-neutral-700 hover:border-navy"}`}
                  data-testid={`activity-type-${p.slug || "all"}`}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="filter-row">
            {isEatDrinkMode && (
              <div className="flex flex-wrap gap-2" data-testid="eat-drink-pills">
                {EAT_DRINK_PILLS.map((p) => {
                  const active = subCategory === p.slug;
                  return (
                    <button
                      key={p.slug || "all"}
                      onClick={() => setFilter("sub", p.slug)}
                      className={`px-4 py-1.5 rounded-full text-sm transition-colors ${active ? "bg-navy text-white" : "bg-white border border-black/10 text-neutral-700 hover:border-navy"}`}
                      data-testid={`eat-drink-${p.slug || "all"}`}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </div>
            )}
            <label className="ml-auto flex items-center gap-2 text-sm text-neutral-600">
              <input
                type="checkbox"
                checked={openNow}
                onChange={(e) => setFilter("open_now", e.target.checked ? "1" : "")}
                className="accent-navy"
                data-testid="filter-open-now"
              />
              Open now
            </label>
          </div>
        )}

        {loading ? (
          <SkeletonGrid count={6} />
        ) : isToursMode ? (
          tours.length === 0 ? (
            <div className="text-center py-20 text-neutral-500">No experiences match your filters. Try clearing them.</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {tours.map((t, i) => <TourCard key={t.id} tour={t} index={i} />)}
            </div>
          )
        ) : venues.length === 0 ? (
          <div className="text-center py-20 text-neutral-500">No venues match your filters. Try clearing them.</div>
        ) : view === "map" ? (
          <SplitDirectoryView venues={venues} />
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {venues.map((v, i) => <VenueCard key={v.id} venue={v} index={i} />)}
          </div>
        )}
      </div>
    </Layout>
  );
}
